import { useEffect } from 'react';
import ConversationList from './components/ConversationList';
import ChatPane from './components/ChatPane';
import ResultsPane from './components/ResultsPane';
import { useChatStore, configureGQSearch } from './store/useChatStore';
import AccountService from '../../services/accountInfo';
import { get } from '../../helpers/fetchServicesMethods';
import './gqsearch.css';

// GQ Search Assistant — rendered as an in-app FullText route (/gqsearch), below the
// FullText header.
export default function GQSearch() {
  const loadConversations = useChatStore((s) => s.loadConversations);
  const restoreConversation = useChatStore((s) => s.restoreConversation);
  const setGqApiToken = useChatStore((s) => s.setGqApiToken);

  useEffect(() => {
    let cancelled = false;

    // Identity for X-User-Id (conversation ownership), same pattern as GQSupport.
    AccountService.getAccountInfo()
      .then((res) => {
        if (!cancelled && res?.response_content) {
          const info = res.response_content;
          configureGQSearch(info.email_address || info.login_name);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          loadConversations();
          // Re-open whatever conversation was active before a refresh (per-tab).
          restoreConversation();
        }
      });

    // Pull the user's real GQ apitoken (access_key) from the already-logged-in session
    // so the user never has to paste one. gquser.get_apitoken is session-based (no
    // credentials — it mints/refreshes the access_key server-side without a password,
    // unlike gquser.get_token which requires username+password). The backend forwards
    // it to the search MCP tools as X-GQ-ApiToken, and those tools authenticate to GQ
    // over the internet — so the apitoken (not the clitoken) is the correct credential.
    // The manual "Set GQ Token" control remains as an override if a specific token is
    // needed.
    const fetchApiToken = () => get('do=gquser.get_apitoken&format=json')
      .then((res) => {
        const token = res?.response_content?.token;
        if (!cancelled && token) setGqApiToken(token);
      })
      .catch(() => {});
    fetchApiToken();
    // The GQ apitoken has a short TTL; re-mint it periodically so we never send a stale
    // token to the search tools (each call extends/refreshes the user's access_key).
    const tokenTimer = setInterval(fetchApiToken, 15 * 60 * 1000); // every 15 min

    return () => { cancelled = true; clearInterval(tokenTimer); };
  }, [loadConversations, restoreConversation, setGqApiToken]);

  return (
    <div
      className="gqsearch-root"
      style={{
        display: 'flex',
        height: 'calc(100vh - 140px)',
        minHeight: 480,
        border: '1px solid var(--gqs-border)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <ConversationList />
      <ChatPane />
      <ResultsPane />
    </div>
  );
}
